import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Wrench, AlertTriangle, CheckCircle2, Clock, X, Check, Car, ChevronRight, Truck, Plus, Loader2, Trash2, Download, Mail,
} from "lucide-react";
import api from "../lib/api";
import WhatsAppButton from "./WhatsAppButton";

/* ------------------------------------------------------------------ *
 * Pre-delivery mechanic-shop revisions.                              *
 * Renders right above the regular Tasks list. Owner / gerente only.  *
 * ------------------------------------------------------------------ */

const STATUS_LABEL = {
  pending: "Esperando ir pra revisão",
  in_shop: "Na oficina",
  ready:   "Pronto pra entregar",
  none:    "Aguardando definição",
};
const STATUS_COLOR = {
  pending: { dot: "bg-warning", chip: "border-warning/50 text-warning" },
  in_shop: { dot: "bg-blue-500", chip: "border-blue-500/50 text-blue-400" },
  ready:   { dot: "bg-success", chip: "border-success/50 text-success" },
  none:    { dot: "bg-primary", chip: "border-primary/50 text-primary" },
};

// Grouped checklist — organized by workshop workflow so the picker feels
// like a real pre-delivery inspection form instead of a random pile of
// checkboxes. Order matters (matches the order a mechanic would work
// through the car). Each item has a stable key that persists in the DB.
const CHECKLIST_GROUPS = [
  {
    title: "Motor & Fluidos",
    items: [
      { key: "oil",         label: "Óleo + filtro" },
      { key: "coolant",     label: "Fluido coolant" },
      { key: "brake_fluid", label: "Fluido de freio" },
      { key: "battery",     label: "Bateria" },
    ],
  },
  {
    title: "Freios",
    items: [
      { key: "brake_discs", label: "Discos de freio" },
      { key: "brake_pads",  label: "Pastilhas de freio" },
    ],
  },
  {
    title: "Pneus & Rodagem",
    items: [
      { key: "tires",         label: "Pneus" },
      { key: "tire_pressure", label: "Pressão dos pneus" },
      { key: "alignment",     label: "Alinhamento" },
      { key: "spare_tire",    label: "Step (estepe)" },
    ],
  },
  {
    title: "Elétrica & Conforto",
    items: [
      { key: "ac",           label: "Ar-condicionado" },
      { key: "lights",       label: "Luzes (farol e lanterna)" },
      { key: "wipers",       label: "Palhetas do limpador" },
      { key: "washer_fluid", label: "Fluido do limpador" },
    ],
  },
  {
    title: "Finalização",
    items: [
      { key: "detail", label: "Detail / lavagem" },
    ],
  },
];

// Flat lookup for legacy code paths that still index by key.
const CHECKLIST_LABELS = Object.fromEntries(
  CHECKLIST_GROUPS.flatMap((g) => g.items.map((i) => [i.key, i.label]))
);
// Include the sentinel "other" for legacy consumers.
CHECKLIST_LABELS.other = "Outros";

export default function RevisionsSection({ t, onChanged, readOnly = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDone, setShowDone] = useState(false);
  // Filter pills — "all" is the default, others narrow the list down. We treat
  // status="none" and "pending" together under "waiting" because the user
  // mentally sees them both as "ainda nem foi pra oficina".
  const [filter, setFilter] = useState("all");
  // "Escolher carros" picker modal — owner explicitly opts-in sold cars to
  // the revision queue instead of every sold car auto-flooding the list.
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/revisions");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || (t ? t("error_generic") : "Erro"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Roll-ups feed both the header chips and the pill counts.
  const open = rows.filter((r) => r.status !== "ready");
  const ready = rows.filter((r) => r.status === "ready");
  const urgent = open.filter((r) => r.is_urgent);
  const waiting = open.filter((r) => r.status === "pending" || r.status === "none");
  const inShop = open.filter((r) => r.status === "in_shop");

  // Apply pill filter, then optionally hide finished revisions.
  const matchesPill = (r) => {
    if (filter === "all") return true;
    if (filter === "waiting") return r.status === "pending" || r.status === "none";
    if (filter === "in_shop") return r.status === "in_shop";
    if (filter === "ready") return r.status === "ready";
    if (filter === "urgent") return r.is_urgent;
    return true;
  };
  let visible = rows.filter(matchesPill);
  // "Mostrar prontas" toggle still applies, but only when the active filter
  // wouldn't naturally include them (i.e. NOT "all"/"ready").
  if (!showDone && filter !== "ready") {
    visible = visible.filter((r) => r.status !== "ready");
  }

  const PILLS = [
    { key: "all",     label: "Todos",      count: open.length, color: "primary" },
    { key: "waiting", label: "Aguardando", count: waiting.length, color: "warning" },
    { key: "in_shop", label: "Na oficina", count: inShop.length, color: "blue" },
    { key: "ready",   label: "Prontos",    count: ready.length, color: "success" },
    { key: "urgent",  label: "Urgentes",   count: urgent.length, color: "primary", emphasis: true },
  ];

  return (
    <section className="space-y-3" data-testid="revisions-section">
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <p className="label-eyebrow text-primary mb-1 inline-flex items-center gap-1">
            <Wrench size={11} /> Revisões pré-entrega
          </p>
          <h2 className="font-display font-black text-xl uppercase tracking-tighter">
            Carros que precisam de revisão
          </h2>
          <p className="text-xs text-text-secondary mt-1">
            Acompanhamento dos carros vendidos que ainda precisam passar pela oficina antes de entregar.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-text-secondary flex-wrap">
          {urgent.length > 0 && (
            <span className="text-warning inline-flex items-center gap-1 font-display font-bold">
              <AlertTriangle size={11} /> {urgent.length} urgente{urgent.length !== 1 && "s"}
            </span>
          )}
          {urgent.length > 0 && <span>·</span>}
          <span>{open.length} em aberto</span>
          {ready.length > 0 && (
            <>
              <span>·</span>
              <span className="text-success font-display font-bold">{ready.length} prontas</span>
            </>
          )}
          <button
            type="button"
            data-testid="revisions-open-picker"
            onClick={() => setPickerOpen(true)}
            className={`ml-2 px-3 py-1.5 border-2 border-primary text-primary hover:bg-primary hover:text-white text-[10px] uppercase tracking-widest font-display font-bold transition-colors inline-flex items-center gap-1.5 ${readOnly ? "hidden" : ""}`}
          >
            <Plus size={11} /> Escolher carros
          </button>
        </div>
      </header>

      {/* FILTER PILLS — quick visual narrow-down. Counts derived from `rows`
          so they stay accurate even as the user clicks around. */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="revisions-filter">
        {PILLS.map((p) => {
          const active = filter === p.key;
          const isEmpty = p.count === 0;
          // Color tokens — keep one set of classes per "color" for clarity.
          const tone =
            p.color === "warning" ? { activeBg: "bg-warning text-black border-warning", idleText: "text-warning border-warning/40" }
            : p.color === "blue"   ? { activeBg: "bg-blue-500 text-white border-blue-500", idleText: "text-blue-400 border-blue-500/40" }
            : p.color === "success" ? { activeBg: "bg-success text-white border-success", idleText: "text-success border-success/40" }
            : { activeBg: "bg-primary text-white border-primary", idleText: "text-primary border-primary/40" };
          return (
            <button
              key={p.key}
              type="button"
              data-testid={`revisions-pill-${p.key}`}
              onClick={() => setFilter(p.key)}
              disabled={isEmpty && !active}
              className={[
                "inline-flex items-center gap-2 px-3 py-1.5 border text-[10px] uppercase tracking-widest font-display font-bold transition-colors",
                active
                  ? tone.activeBg
                  : isEmpty
                    ? "border-border text-text-secondary/40 cursor-not-allowed"
                    : `bg-surface ${tone.idleText} hover:border-primary hover:text-primary`,
                p.emphasis && !active && p.count > 0 ? "ring-1 ring-inset ring-warning/30" : "",
              ].join(" ")}
            >
              {p.key === "urgent" && p.count > 0 && <AlertTriangle size={10} />}
              {p.label}
              <span
                className={[
                  "px-1.5 py-0 text-[9px] tabular-nums",
                  active
                    ? "bg-white/15 border border-white/30"
                    : "border border-current/30",
                ].join(" ")}
              >
                {p.count}
              </span>
            </button>
          );
        })}
        {/* Allow opting in to seeing the "Prontas" rows when not on that filter */}
        {filter !== "ready" && ready.length > 0 && (
          <button
            type="button"
            data-testid="revisions-show-done-toggle"
            onClick={() => setShowDone((s) => !s)}
            className="ml-1 px-2 py-1.5 border border-border text-[10px] uppercase tracking-widest text-text-secondary hover:border-primary hover:text-primary transition-colors"
          >
            {showDone ? "Ocultar prontas" : "Mostrar prontas"}
          </button>
        )}
        {filter !== "all" && (
          <button
            type="button"
            data-testid="revisions-clear-filter"
            onClick={() => setFilter("all")}
            className="ml-1 text-[10px] uppercase tracking-widest text-text-secondary hover:text-primary inline-flex items-center gap-1"
          >
            <X size={11} /> Limpar
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-text-secondary text-xs text-center py-6">…</p>
      ) : visible.length === 0 ? (
        <div className="border border-border bg-surface/40 p-8 text-center">
          <CheckCircle2 size={28} className="mx-auto mb-2 text-success" />
          <p className="text-text-secondary text-sm">
            {filter === "all"
              ? (readOnly
                  ? "Nenhum carro na fila de revisão no momento."
                  : "Nenhum carro na fila de revisão. Use \"Escolher carros\" para adicionar.")
              : "Nenhum carro nesse filtro. Tente outra pílula."}
          </p>
          {filter === "all" && !readOnly && (
            <button
              type="button"
              data-testid="revisions-empty-picker"
              onClick={() => setPickerOpen(true)}
              className="mt-4 px-4 py-2 bg-primary text-white text-[10px] uppercase tracking-widest font-display font-bold hover:bg-primary/90 inline-flex items-center gap-1.5"
            >
              <Plus size={11} /> Escolher carros
            </button>
          )}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="revisions-list">
          {visible.map((r) => (
            <RevisionRow
              key={r.vehicle_id}
              row={r}
              onEdit={() => setEditing(r)}
            />
          ))}
        </ul>
      )}

      {editing && (
        <RevisionModal
          row={editing}
          readOnly={readOnly}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
            if (onChanged) onChanged();
          }}
        />
      )}

      {pickerOpen && (
        <RevisionPickerModal
          onClose={() => setPickerOpen(false)}
          onConfirmed={() => {
            setPickerOpen(false);
            load();
            if (onChanged) onChanged();
          }}
        />
      )}
    </section>
  );
}

function RevisionRow({ row, onEdit }) {
  const statusKey = row.status === "none" ? "none" : row.status;
  const meta = STATUS_COLOR[statusKey];
  const vinTail = row.vin ? String(row.vin).slice(-6).toUpperCase() : "";
  const label = `${row.year || ""} ${row.make || ""} ${row.model || ""}`.trim();

  return (
    <li
      data-testid={`revision-row-${row.vehicle_id}`}
      data-urgent={row.is_urgent || undefined}
      data-status={row.status}
      className={`bg-surface border ${row.is_urgent ? "border-warning/60 ring-1 ring-inset ring-warning/20" : "border-border"} hover:border-primary/50 transition-colors`}
    >
      <button
        type="button"
        onClick={onEdit}
        className="w-full flex items-stretch gap-3 p-3 text-left"
      >
        {/* Thumbnail */}
        {row.image ? (
          <img src={row.image} alt="" className="w-16 h-14 object-cover border border-border shrink-0" loading="lazy" />
        ) : (
          <div className="w-16 h-14 grid place-items-center bg-background border border-border text-text-secondary shrink-0">
            <Car size={18} />
          </div>
        )}

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${meta.chip}`}>
              <span className={`w-1.5 h-1.5 ${meta.dot}`} />
              {STATUS_LABEL[statusKey]}
            </span>
            {row.is_urgent && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-warning text-black font-display font-black">
                <AlertTriangle size={9} /> Urgente
              </span>
            )}
            {row.days_open > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] text-text-secondary">
                <Clock size={9} /> {row.days_open}d
              </span>
            )}
          </div>
          <p className="font-display font-bold text-sm mt-1 truncate">{label}</p>
          <p className="text-[10px] text-text-secondary truncate">
            {vinTail && <span className="font-mono">{vinTail}</span>}
            {row.buyer_name && <> · 👤 {row.buyer_name}</>}
            {row.salesperson_name && <> · {row.salesperson_name}</>}
            {row.shop_name && <> · 🔧 {row.shop_name}</>}
          </p>
        </div>

        <ChevronRight size={16} className="text-text-secondary shrink-0 self-center" />
      </button>
    </li>
  );
}

function RevisionModal({ row, onClose, onSaved, readOnly = false }) {
  const [status, setStatus] = useState(row.status === "none" ? "pending" : row.status);
  const [shopName, setShopName] = useState(row.shop_name || "");
  const [sentAt, setSentAt] = useState(row.sent_at || "");
  const [expectedBack, setExpectedBack] = useState(row.expected_back_at || "");
  const [notes, setNotes] = useState(row.notes || "");
  const cl = row.checklist || {};
  // Legacy migration: old revisions stored a single `brakes` field for both
  // discs + pads. Split forward: if `brakes` was true we mark BOTH the new
  // `brake_discs` and `brake_pads` as done so nothing is lost when the user
  // reopens an old card.
  const legacyBrakes = !!cl.brakes;
  const [checklist, setChecklist] = useState({
    // Motor & Fluidos
    oil:           !!cl.oil,
    coolant:       !!cl.coolant,
    brake_fluid:   !!cl.brake_fluid,
    battery:       !!cl.battery,
    // Freios (formerly combined `brakes`)
    brake_discs:   cl.brake_discs !== undefined ? !!cl.brake_discs : legacyBrakes,
    brake_pads:    cl.brake_pads  !== undefined ? !!cl.brake_pads  : legacyBrakes,
    // Pneus & Rodagem
    tires:         !!cl.tires,
    tire_pressure: !!cl.tire_pressure,
    alignment:     !!cl.alignment,
    spare_tire:    !!cl.spare_tire,
    // Elétrica & Conforto
    ac:            !!cl.ac,
    lights:        !!cl.lights,
    wipers:        !!cl.wipers,
    washer_fluid:  !!cl.washer_fluid,
    // Finalização
    detail:        !!cl.detail,
    // Sentinel
    other:         !!cl.other,
    other_note:    cl.other_note || "",
  });
  const [saving, setSaving] = useState(false);

  const setCL = (k, v) => setChecklist((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/vehicles/${row.vehicle_id}/revision`, {
        status,
        shop_name: shopName,
        sent_at: sentAt || null,
        expected_back_at: expectedBack || null,
        notes,
        checklist,
      });
      toast.success("Revisão atualizada");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Remover a revisão deste carro?")) return;
    setSaving(true);
    try {
      await api.put(`/vehicles/${row.vehicle_id}/revision`, { status: "none" });
      toast.success("Revisão removida");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const vinTail = row.vin ? String(row.vin).slice(-6).toUpperCase() : "";
  const carLabel = `${row.year || ""} ${row.make || ""} ${row.model || ""}`.trim();

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="revision-modal"
        className="bg-background border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div>
            <p className="label-eyebrow text-primary mb-1 inline-flex items-center gap-1">
              <Wrench size={11} /> Revisão pré-entrega
            </p>
            <h3 className="font-display font-black text-lg uppercase tracking-tighter">{carLabel}</h3>
            <p className="text-[11px] text-text-secondary mt-1">
              {vinTail && <span className="font-mono">{vinTail}</span>}
              {row.buyer_name && <> · 👤 {row.buyer_name}</>}
            </p>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-primary transition-colors">
            <X size={20} />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {readOnly && (
            <div className="bg-info/10 border border-info/40 px-3 py-2 flex items-center gap-2 text-info">
              <AlertTriangle size={14} />
              <span className="text-[11px] uppercase tracking-widest font-display font-bold">
                Modo somente leitura — apenas gestores e mecânicos editam
              </span>
            </div>
          )}
          <fieldset disabled={readOnly} className="space-y-5 disabled:opacity-90">
          {/* Status selector */}
          <div>
            <p className="label-eyebrow mb-2">Status</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "pending", l: "Esperando", icon: Clock, color: "warning" },
                { v: "in_shop", l: "Na oficina", icon: Wrench, color: "blue-500" },
                { v: "ready", l: "Pronto", icon: Truck, color: "success" },
              ].map((opt) => {
                const active = status === opt.v;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`revision-status-${opt.v}`}
                    onClick={() => setStatus(opt.v)}
                    className={`px-3 py-3 border text-center transition-colors ${
                      active
                        ? opt.color === "warning"
                          ? "bg-warning text-black border-warning"
                          : opt.color === "success"
                            ? "bg-success text-white border-success"
                            : "bg-blue-500 text-white border-blue-500"
                        : "bg-surface border-border hover:border-primary"
                    }`}
                  >
                    <Icon size={16} className="mx-auto mb-1" />
                    <span className="text-[10px] uppercase tracking-widest font-display font-bold">{opt.l}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Shop info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="label-eyebrow mb-2">Oficina</p>
              <input
                data-testid="revision-shop-name"
                value={shopName}
                onChange={(e) => setShopName(e.target.value)}
                placeholder="Ex: Mecânica do Zé"
                className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm"
              />
            </div>
            <div>
              <p className="label-eyebrow mb-2">Previsão de retorno</p>
              <input
                type="date"
                data-testid="revision-expected-back"
                value={expectedBack}
                onChange={(e) => setExpectedBack(e.target.value)}
                className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm"
              />
            </div>
            <div>
              <p className="label-eyebrow mb-2">Enviado em</p>
              <input
                type="date"
                data-testid="revision-sent-at"
                value={sentAt}
                onChange={(e) => setSentAt(e.target.value)}
                className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm"
              />
            </div>
          </div>

          {/* Checklist */}
          <div>
            <p className="label-eyebrow mb-3">Checklist da revisão</p>
            <div className="space-y-4">
              {CHECKLIST_GROUPS.map((group) => (
                <div key={group.title}>
                  <p className="text-[10px] uppercase tracking-widest text-primary font-display font-black mb-2 pb-1 border-b border-primary/30">
                    {group.title}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {group.items.map(({ key, label }) => {
                      const v = checklist[key];
                      return (
                        <label
                          key={key}
                          data-testid={`revision-cl-${key}`}
                          className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors ${
                            v ? "border-success/60 bg-success/[0.06]" : "border-border bg-surface hover:border-primary"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={v}
                            onChange={(e) => setCL(key, e.target.checked)}
                            className="accent-success"
                          />
                          <span className="text-sm font-display font-bold flex items-center gap-1">
                            {v && <Check size={12} className="text-success" />}
                            {label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Outros — sentinel + free-text note */}
              <div>
                <p className="text-[10px] uppercase tracking-widest text-primary font-display font-black mb-2 pb-1 border-b border-primary/30">
                  Outros
                </p>
                <label
                  data-testid="revision-cl-other"
                  className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors ${
                    checklist.other ? "border-success/60 bg-success/[0.06]" : "border-border bg-surface hover:border-primary"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checklist.other}
                    onChange={(e) => setCL("other", e.target.checked)}
                    className="accent-success"
                  />
                  <span className="text-sm font-display font-bold">Outros serviços realizados</span>
                </label>
                {checklist.other && (
                  <input
                    data-testid="revision-cl-other-note"
                    value={checklist.other_note}
                    onChange={(e) => setCL("other_note", e.target.value)}
                    placeholder="Descreva o que mais foi feito (ex: troca de correia dentada, reparo no ar-condicionado...)"
                    className="w-full mt-2 bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="label-eyebrow mb-2">Notas / observações</p>
            <textarea
              data-testid="revision-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Ex: trocou pastilha de freio, faltou alinhar, cliente vai aceitar entrega quarta-feira..."
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm resize-y"
            />
          </div>

          {status === "ready" && (
            <div className="bg-success/10 border border-success/40 p-3 text-xs text-success leading-snug">
              ✅ Ao marcar como pronto, você + os gerentes receberão um email
              automático avisando que o carro pode ser agendado pra entrega.
            </div>
          )}
          </fieldset>
        </div>

        <footer className="p-5 border-t border-border flex items-center justify-between gap-3 flex-wrap">
          {!readOnly && row.status !== "none" && (
            <button
              type="button"
              data-testid="revision-clear"
              onClick={clearAll}
              disabled={saving}
              className="text-[10px] uppercase tracking-widest text-text-secondary hover:text-primary inline-flex items-center gap-1"
            >
              <X size={12} /> Remover revisão
            </button>
          )}
          <button
            type="button"
            data-testid="revision-download-pdf"
            onClick={async () => {
              try {
                const res = await api.get(`/vehicles/${row.vehicle_id}/revision-receipt.pdf`, { responseType: "blob" });
                const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = `comprovante-revisao-${carLabel.replace(/\s+/g, "_") || "carro"}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(blobUrl);
                toast.success("Comprovante gerado");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erro ao gerar PDF");
              }
            }}
            className="text-[10px] uppercase tracking-widest text-text-secondary hover:text-primary inline-flex items-center gap-1"
            title="Gerar um comprovante em PDF pra imprimir/mandar pro cliente"
          >
            <Download size={12} /> Comprovante PDF
          </button>
          <button
            type="button"
            data-testid="revision-email-pdf"
            onClick={async () => {
              // 1-click path: if the buyer's email is already on file (which
              // is now required at sale time), fire directly. If it's blank
              // we still fall back to a prompt for legacy vehicles that were
              // sold before the required-field change.
              const stored = (row.buyer_email || "").trim();
              let to = stored;
              if (!to) {
                to = window.prompt(
                  `E-mail do cliente${row.buyer_name ? " (" + row.buyer_name + ")" : ""}?\n(o time da loja também recebe uma cópia automaticamente)`,
                  ""
                );
                if (to === null) return;
              }
              try {
                const res = await api.post(`/vehicles/${row.vehicle_id}/revision-receipt/email`, {
                  to: (to || "").trim(),
                  include_team: true,
                });
                const list = (res.data && res.data.sent_to) || [];
                toast.success(
                  list.length
                    ? `Enviado para ${list.length} destinatário${list.length > 1 ? "s" : ""}`
                    : "Comprovante enviado"
                );
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erro ao enviar e-mail");
              }
            }}
            className="text-[10px] uppercase tracking-widest text-text-secondary hover:text-primary inline-flex items-center gap-1"
            title="Enviar o comprovante por e-mail pro cliente + equipe da loja"
          >
            <Mail size={12} /> Enviar e-mail
          </button>
          {status === "ready" && row.buyer_phone && (
            <WhatsAppButton
              phone={row.buyer_phone}
              testid="revision-whatsapp-buyer"
              variant="chip"
              label="Avisar cliente"
              message={
                `Olá${row.buyer_name ? " " + row.buyer_name : ""}! Boa notícia: o seu ${carLabel || "carro"}` +
                ` acabou de sair da revisão e está pronto pra retirada. Combinamos um horário?`
              }
            />
          )}
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 h-10 border border-border hover:border-primary text-text-secondary hover:text-primary text-[11px] uppercase tracking-widest font-display font-bold transition-colors"
            >
              {readOnly ? "Fechar" : "Cancelar"}
            </button>
            {!readOnly && (
              <button
                type="button"
                data-testid="revision-save"
                onClick={save}
                disabled={saving}
                className="bg-primary hover:bg-primary-hover disabled:opacity-50 text-white px-5 h-10 inline-flex items-center gap-2 text-[11px] font-display font-bold uppercase tracking-widest"
              >
                <Check size={14} /> Salvar revisão
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}


/**
 * RevisionPickerModal
 * --------------------------------------------------------------------------
 * Lets the owner pick (in bulk) which sold-not-yet-delivered cars enter
 * the revision queue. Uses GET /api/revisions/candidates to list the cars
 * NOT yet in revision, then POST /api/revisions/select with the chosen
 * vehicle ids.
 */
function RevisionPickerModal({ onClose, onConfirmed }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/revisions/candidates");
        setCandidates(res.data || []);
      } catch {
        toast.error("Erro ao carregar lista");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = candidates.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(c.year).includes(q) ||
      (c.make || "").toLowerCase().includes(q) ||
      (c.model || "").toLowerCase().includes(q) ||
      (c.vin || "").toLowerCase().includes(q) ||
      (c.buyer_name || "").toLowerCase().includes(q)
    );
  });

  const toggle = (vid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(vid)) next.delete(vid);
      else next.add(vid);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((c) => c.vehicle_id)));
    }
  };

  const confirm = async () => {
    if (selected.size === 0) {
      toast.error("Selecione pelo menos 1 carro");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/revisions/select", {
        vehicle_ids: Array.from(selected),
      });
      toast.success(`${res.data.added} carro(s) adicionados à revisão`);
      onConfirmed && onConfirmed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="revision-picker-modal"
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-background border border-border w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        <header className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <p className="label-eyebrow text-primary">Adicionar à revisão</p>
            <h3 className="font-display font-black text-xl uppercase tracking-tighter mt-1">
              Escolher carros para revisão
            </h3>
          </div>
          <button
            type="button"
            data-testid="picker-close"
            onClick={onClose}
            className="text-text-secondary hover:text-primary"
          >
            <X size={20} />
          </button>
        </header>

        <div className="px-5 py-3 border-b border-border flex items-center gap-3 flex-wrap">
          <input
            data-testid="picker-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar (marca, modelo, VIN, comprador...)"
            className="flex-1 min-w-[200px] h-10 bg-surface border border-border focus:border-primary focus:outline-none px-3 text-sm"
          />
          {filtered.length > 0 && (
            <button
              type="button"
              data-testid="picker-toggle-all"
              onClick={toggleAll}
              className="px-3 h-10 border border-border hover:border-primary text-[10px] uppercase tracking-widest font-display font-bold"
            >
              {selected.size === filtered.length ? "Limpar" : `Marcar todos (${filtered.length})`}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <p className="text-center text-text-secondary text-sm py-8">
              <Loader2 size={16} className="inline animate-spin mr-2" /> Carregando…
            </p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 size={28} className="mx-auto mb-2 text-success" />
              <p className="text-text-secondary text-sm">
                {search.trim()
                  ? "Nenhum carro encontrado."
                  : "Todos os carros vendidos já estão na fila de revisão ou foram entregues."}
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {filtered.map((c) => {
                const isPicked = selected.has(c.vehicle_id);
                const isUrgent = (c.days_since_sale || 0) > 10;
                return (
                  <li
                    key={c.vehicle_id}
                    data-testid={`picker-row-${c.vehicle_id}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(c.vehicle_id)}
                      className={`w-full flex items-center gap-3 p-3 border text-left transition-colors ${
                        isPicked
                          ? "bg-primary/10 border-primary"
                          : "bg-surface border-border hover:border-primary/50"
                      }`}
                    >
                      <span
                        className={`w-5 h-5 border-2 inline-flex items-center justify-center shrink-0 ${
                          isPicked ? "bg-primary border-primary" : "border-border"
                        }`}
                      >
                        {isPicked && <Check size={12} className="text-white" />}
                      </span>
                      {c.image ? (
                        <img src={c.image} alt="" className="w-14 h-12 object-cover border border-border shrink-0" />
                      ) : (
                        <div className="w-14 h-12 bg-background border border-border flex items-center justify-center shrink-0">
                          <Car size={18} className="text-text-secondary" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-sm truncate">
                          {c.year} {c.make} {c.model}
                          {c.status && c.status !== "sold" && (
                            <span className="ml-2 text-[9px] uppercase tracking-widest border border-info/50 text-info bg-info/10 px-1.5 py-0.5 font-display font-bold">
                              {c.status === "in_stock" ? "ESTOQUE" : c.status === "reserved" ? "RESERVADO" : c.status.toUpperCase()}
                            </span>
                          )}
                          {isUrgent && (
                            <span className="ml-2 text-[9px] uppercase tracking-widest text-warning inline-flex items-center gap-0.5">
                              <AlertTriangle size={10} /> URGENTE
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] text-text-secondary uppercase tracking-widest mt-0.5 truncate">
                          {c.status === "sold"
                            ? (
                                <>
                                  {c.buyer_name || "—"}
                                  {c.vin && <> · VIN {String(c.vin).slice(-6).toUpperCase()}</>}
                                  {" · "}
                                  {c.days_since_sale > 0
                                    ? `Vendido há ${c.days_since_sale}d`
                                    : "Vendido hoje"}
                                </>
                              )
                            : (
                                <>
                                  {c.vin ? <>VIN {String(c.vin).slice(-6).toUpperCase()}</> : "Sem VIN"}
                                  {" · "}
                                  Aguardando venda
                                </>
                              )}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="p-4 border-t border-border flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-text-secondary">
            <strong className="text-white">{selected.size}</strong> selecionado{selected.size !== 1 && "s"}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="picker-cancel"
              onClick={onClose}
              className="border border-border text-text-secondary hover:text-primary hover:border-primary px-4 h-10 text-[11px] font-display font-bold uppercase tracking-widest"
            >
              Cancelar
            </button>
            <button
              type="button"
              data-testid="picker-confirm"
              onClick={confirm}
              disabled={saving || selected.size === 0}
              className="bg-primary hover:bg-primary-hover disabled:opacity-50 text-white px-5 h-10 inline-flex items-center gap-2 text-[11px] font-display font-bold uppercase tracking-widest"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Adicionar à revisão
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
